import * as React from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';

export interface DimensionObject {
  width: number;
  height: number;
  top: number;
  left: number;
  x: number;
  y: number;
  right: number;
  bottom: number;
}

export interface UseDimensionParams {
  ref: React.RefObject<HTMLElement>;
  liveMeasure?: boolean;
}

export function useDimension(params: UseDimensionParams): Partial<DimensionObject> {
  const { liveMeasure } = params;
  const [dimensions, setDimensions] = useState({});
  const [node, setNode] = useState<HTMLElement | null>(null);

  const requestRef = React.useRef<number>();

  const getDimensionObject = (elem: HTMLElement): DimensionObject => {
    const rect: any = elem.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: 'x' in rect ? rect.x : rect.top,
      left: 'y' in rect ? rect.y : rect.left,
      x: 'x' in rect ? rect.x : rect.left,
      y: 'y' in rect ? rect.y : rect.top,
      right: rect.right,
      bottom: rect.bottom
    };
  };

  useEffect(() => {
    if (params.ref.current) {
      setNode(params.ref.current);
    }
  }, [params.ref.current]);

  const measure = () =>
    (requestRef.current = window.requestAnimationFrame(() => {
      if (!node) {
        return;
      }
      setDimensions(getDimensionObject(node));
    }));

  useLayoutEffect(() => {
    if (node) {
      measure();

      if (liveMeasure) {
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure);

        return () => {
          !!requestRef.current && cancelAnimationFrame(requestRef.current);
          window.removeEventListener('resize', measure);
          window.removeEventListener('scroll', measure);
        };
      }
    }
    return () => !!requestRef.current && cancelAnimationFrame(requestRef.current);
  }, [node]);

  return dimensions;
}
